/**
 * LevelLoader - Carga y renderiza niveles desde JSON
 */

import * as Phaser from 'phaser';
import type {
  LevelData,
  LayerData,
  ObjectInstance,
  CollectibleData,
  SpawnData,
  TilemapInstance
} from '../data/LevelFormat';
import { UID } from './UIDGenerator';
import { LayerDepths } from '../constants/enums';
import type { CollisionSystem } from '../systems/CollisionSystem';

// Layout data from layouts-c3data.json (SSOT for layout behaviors)
interface LayoutDefinition {
  width: number;
  height: number;
  originx: number;
  originy: number;
  plugintype: string;
  hascollision: boolean;
  behaviors?: {
    solid?: Record<string, unknown>;
  };
}

interface LayoutsC3Data {
  layouts: Record<string, LayoutDefinition>;
}

// Grupo de containers por capa para organizar el renderizado
interface LevelContainers {
  layers: Map<string, Phaser.GameObjects.Container>;
  collectibles: Phaser.GameObjects.Container;
  npcs: Phaser.GameObjects.Container;
}

// Resultado del nivel cargado
export interface LoadedLevel {
  data: LevelData;
  containers: LevelContainers;
  collectibles: Phaser.GameObjects.Sprite[];
  npcs: Phaser.GameObjects.Sprite[];
  effectiveWidth: number;   // Width con override aplicado
  effectiveHeight: number;  // Height con override aplicado
}

/**
 * Carga un nivel desde JSON y lo renderiza en la escena
 */
// Tipo para overrides de tilemaps (visual + data)
interface TilemapOverride {
  scale?: number;
  offsetX?: number;
  offsetY?: number;
  gridHeight?: number;  // Override del gridHeight de C3 (toma filas desde abajo)
  y?: number;           // Override posición Y base
  tiles?: number[];     // Override array de tiles
}

// Tipo para overrides de layouts (solo visual)
interface LayoutOverride {
  scale?: number;
  offsetX?: number;
  offsetY?: number;
  x?: number;        // Override posición X absoluta (reemplaza obj.x, ignora offsetX)
  y?: number;        // Override posición Y absoluta (reemplaza obj.y, ignora offsetY)
  width?: number;    // Override del ancho (reemplaza obj.width)
  height?: number;   // Override del alto (reemplaza obj.height)
  originX?: number;  // Override del origin X (default: obj.originX ?? 0.5)
  originY?: number;  // Override del origin Y (default: obj.originY ?? 0.5)
}

// Estructura completa del archivo de overrides
interface OverridesData {
  aliases?: Record<string, string>;
  global?: {
    tilemaps?: Record<string, TilemapOverride>;
    layouts?: Record<string, LayoutOverride>;
  };
  levels?: Record<string, {
    width?: number;      // Override del ancho del nivel
    height?: number;     // Override del alto del nivel
    tilemaps?: Record<string, TilemapOverride>;
    layouts?: Record<string, LayoutOverride>;
    exclude?: string[];  // IDs de objetos a excluir (ej: ["#8", "#12"])
  }>;
  instances?: Record<string, Record<string, TilemapOverride | LayoutOverride>>;
}

export class LevelLoader {
  private scene: Phaser.Scene;
  private levelData: LevelData | null = null;
  private currentLevelId: string = '';
  private aliases: Record<string, string> = {};
  private overridesData: OverridesData = {};
  private layoutsData: LayoutsC3Data | null = null;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  /**
   * Check if a layout has solid behavior (SSOT: reads from layouts-c3data.json)
   */
  private hasSolidBehavior(textureKey: string): boolean {
    if (!this.layoutsData) {
      console.error('[LevelLoader] layoutsData not loaded');
      return false;
    }
    const layout = this.layoutsData.layouts[textureKey.toLowerCase()];
    return layout?.behaviors?.solid !== undefined;
  }

  /**
   * Resuelve un nombre C3 a su alias si existe
   * Ej: "sprite2" → "bush1"
   */
  private resolveAlias(c3Name: string): string {
    return this.aliases[c3Name] ?? c3Name;
  }

  /**
   * Precarga el JSON del nivel (llamar en preload)
   */
  preloadLevel(levelId: string): void {
    // Ruta absoluta desde root (public/assets se sirve como /assets)
    this.scene.load.json(`${levelId}-layout-c3data`, `/assets/levels/${levelId}-layout-c3data.json`);
  }

  /**
   * Carga overrides desde el cache (entities-overrides + level-specific overrides)
   */
  private loadOverrides(): void {
    // 0. Cargar layouts-c3data (SSOT para behaviors.solid de TileSprites)
    this.layoutsData = this.scene.cache.json.get('layouts-c3data') as LayoutsC3Data | null;
    if (!this.layoutsData) {
      console.error('[LevelLoader] layouts-c3data not found in cache');
    }

    // 1. Cargar overrides globales (aliases, global tilemaps)
    const entitiesOverrides = this.scene.cache.json.get('entities-overrides') as OverridesData | undefined;
    if (!entitiesOverrides) {
      console.warn('[LevelLoader] entities-overrides not found in cache, using empty defaults');
    }
    this.overridesData = entitiesOverrides ?? {};
    this.aliases = entitiesOverrides?.aliases ?? {};

    // 2. Cargar overrides específicos del nivel y mergear (layout overrides)
    const levelOverrides = this.scene.cache.json.get(`${this.currentLevelId}-layout-overrides`) as {
      width?: number;
      height?: number;
      tilemaps?: Record<string, TilemapOverride>;
      layouts?: Record<string, LayoutOverride>;
      exclude?: string[];
      instances?: Record<string, TilemapOverride | LayoutOverride>;
    } | undefined;

    if (levelOverrides) {
      // Construir estructura levels[levelId] desde el archivo separado
      if (!this.overridesData.levels) {
        this.overridesData.levels = {};
      }
      this.overridesData.levels[this.currentLevelId] = {
        width: levelOverrides.width,
        height: levelOverrides.height,
        tilemaps: levelOverrides.tilemaps,
        layouts: levelOverrides.layouts,
        exclude: levelOverrides.exclude,
      };

      // Construir estructura instances[levelId] desde el archivo separado
      if (levelOverrides.instances) {
        if (!this.overridesData.instances) {
          this.overridesData.instances = {};
        }
        this.overridesData.instances[this.currentLevelId] = levelOverrides.instances;
      }
    }
  }

  /**
   * Resuelve override de layout con prioridad: instance > level
   */
  private getLayoutOverride(textureKey: string, instanceId?: string): LayoutOverride | undefined {
    const key = this.resolveAlias(textureKey);

    // 1. Buscar override por instancia (#n)
    if (instanceId) {
      const instanceOverride = this.overridesData.instances?.[this.currentLevelId]?.[instanceId];
      if (instanceOverride) {
        return instanceOverride as LayoutOverride;
      }
    }

    // 2. Buscar override por nivel
    return this.overridesData.levels?.[this.currentLevelId]?.layouts?.[key];
  }

  /**
   * Resuelve override de tilemap con prioridad: instance > level > global
   */
  private getTilemapOverride(textureKey: string, instanceId?: string): TilemapOverride | undefined {
    const key = this.resolveAlias(textureKey);

    // 1. Buscar override por instancia (#n)
    if (instanceId) {
      const instanceOverride = this.overridesData.instances?.[this.currentLevelId]?.[instanceId];
      if (instanceOverride) {
        return instanceOverride as TilemapOverride;
      }
    }

    // 2. Buscar override por nivel
    const levelOverride = this.overridesData.levels?.[this.currentLevelId]?.tilemaps?.[key];
    if (levelOverride) {
      return levelOverride;
    }

    // 3. Buscar override global
    return this.overridesData.global?.tilemaps?.[key];
  }

  /**
   * Verifica si un objeto debe ser excluido por su ID
   */
  private isExcluded(objectId?: string): boolean {
    if (!objectId) return false;
    const excludeList = this.overridesData.levels?.[this.currentLevelId]?.exclude;
    return excludeList?.includes(objectId) ?? false;
  }

  /**
   * Carga y renderiza el nivel completo (llamar en create)
   */
  async loadLevel(levelId: string): Promise<LoadedLevel> {
    // Obtener datos del cache (layout-c3data)
    this.levelData = this.scene.cache.json.get(`${levelId}-layout-c3data`) as LevelData;

    if (!this.levelData) {
      throw new Error(`Level "${levelId}-layout-c3data" not found in cache. Did you preload it?`);
    }

    // Resetear generador de UIDs para el nuevo nivel
    UID.reset();

    // Guardar levelId y cargar overrides
    this.currentLevelId = levelId;
    this.loadOverrides();

    // Configurar el mundo
    this.setupWorld(this.levelData);

    // Crear containers
    const containers = this.createContainers();

    // Renderizar capas
    for (const layer of this.levelData.layers) {
      this.renderLayer(layer, containers);
    }

    // Renderizar coleccionables
    const collectibles = this.renderCollectibles(
      this.levelData.collectibles,
      containers.collectibles
    );

    // Renderizar NPCs (de las capas Abducteables)
    const npcs = this.extractAndRenderNPCs(containers.npcs);

    // Calcular dimensiones efectivas (con overrides)
    const levelOverrides = this.overridesData.levels?.[this.currentLevelId];
    const effectiveWidth = levelOverrides?.width ?? this.levelData.width;
    const effectiveHeight = levelOverrides?.height ?? this.levelData.height;

    return {
      data: this.levelData,
      containers,
      collectibles,
      npcs,
      effectiveWidth,
      effectiveHeight,
    };
  }

  /**
   * Configura el tamaño del mundo y cámara
   */
  private setupWorld(data: LevelData): void {
    // Obtener dimensiones con override si existe
    const levelOverrides = this.overridesData.levels?.[this.currentLevelId];
    const width = levelOverrides?.width ?? data.width;
    const height = levelOverrides?.height ?? data.height;

    // Configurar bounds del mundo
    this.scene.physics.world.setBounds(0, 0, width, height);

    // Configurar cámara
    const camera = this.scene.cameras.main;
    camera.setBounds(0, 0, width, height);

    // Color de fondo
    if (data.backgroundColor) {
      camera.setBackgroundColor(data.backgroundColor);
    }
  }

  /**
   * Crea los containers para organizar objetos
   */
  private createContainers(): LevelContainers {
    const layers = new Map<string, Phaser.GameObjects.Container>();

    // Container para collectibles (depth alto para estar encima)
    const collectibles = this.scene.add.container(0, 0);
    collectibles.setDepth(100);

    // Container para NPCs
    const npcs = this.scene.add.container(0, 0);
    npcs.setDepth(50);

    return { layers, collectibles, npcs };
  }

  /**
   * Renderiza una capa del nivel
   */
  private renderLayer(layer: LayerData, containers: LevelContainers): void {
    // Excluir capas que maneja el RuntimeManagerSpawn
    const entityLayers = ['Abducteables', 'Destructables'];
    if (entityLayers.includes(layer.name)) {
      return; // RuntimeManagerSpawn se encarga de estas entidades
    }

    // Crear container para esta capa
    const container = this.scene.add.container(0, 0);
    containers.layers.set(layer.name, container);

    // Asignar depth según orden de capas
    const depthMap: Record<string, number> = {
      'Background': LayerDepths.Background,
      'AirBackground': LayerDepths.AirBackground,
      'MountainBackground': LayerDepths.MountainBackground,
      'Floor': LayerDepths.Floor,
      'Destructables': LayerDepths.Destructables,
      'Abducteables': LayerDepths.Abducteables,
      'OverImages': LayerDepths.OverImages,
    };
    container.setDepth(depthMap[layer.name] ?? LayerDepths.Floor);

    // No mostrar si no es visible
    if (!layer.visible) {
      container.setVisible(false);
    }

    // Renderizar cada objeto
    for (const obj of layer.objects) {
      // Verificar si el objeto está excluido
      if (this.isExcluded(obj.id)) {
        continue;
      }
      const gameObj = this.createObject(obj, layer.parallaxX);
      if (gameObj) {
        container.add(gameObj);
      }
    }

    // Renderizar tilemaps de C3
    if (layer.tilemaps) {
      for (const tilemap of layer.tilemaps) {
        // Verificar si el tilemap está excluido
        if (this.isExcluded(tilemap.id)) {
          continue;
        }
        const tilemapObjs = this.createTilemap(tilemap);
        for (const obj of tilemapObjs) {
          container.add(obj);
        }
      }
    }

    // Configurar parallax si no es 1
    if (layer.parallaxX !== 1 || layer.parallaxY !== 1) {
      container.setScrollFactor(layer.parallaxX, layer.parallaxY);
    }
  }

  /**
   * Tipos que se repiten en AMBAS direcciones (width y height del JSON)
   * Ejemplo: fondos tipo mosaico
   */
  private static readonly TILE_BOTH = new Set([
    'tiledbackground',
    'tiledfloor',
  ]);

  /**
   * Tipos que se repiten solo HORIZONTALMENTE (width del JSON, height de textura)
   * Detectados por prefijo o por nombre específico
   * NOTE: tmtreegreen, tmtreeorange, etc. son árboles individuales del tilemap sparse convertido - NO repetir
   */
  private static readonly TILE_HORIZONTAL_PREFIXES: string[] = [];
  private static readonly TILE_HORIZONTAL_NAMES = new Set([
    'bgmazorcas',
    'airbackground6',
    'mountainbackground4',
    'tmvalla',  // Vallas sí se repiten horizontalmente
  ]);

  /**
   * Crea un objeto visual desde ObjectInstance
   */
  private createObject(obj: ObjectInstance, _parallax: number): Phaser.GameObjects.GameObject | null {
    // Verificar si el asset existe
    const textureKey = this.getTextureKey(obj.type);

    if (!this.scene.textures.exists(textureKey)) {
      // Asset no cargado - crear placeholder
      return this.createPlaceholder(obj);
    }

    // Obtener override con prioridad: instance > level > global
    const override = this.getLayoutOverride(textureKey, obj.id);
    const scaleOverride = override?.scale ?? 1;
    const offsetX = override?.offsetX ?? 0;
    const offsetY = override?.offsetY ?? 0;

    // Determinar tipo de tile
    const tileBoth = LevelLoader.TILE_BOTH.has(textureKey);
    const tileHorizontal = this.isTileHorizontal(textureKey);

    if (tileBoth) {
      // Repetir en ambas direcciones (usar width y height del JSON o override)
      const tileX = override?.x ?? (obj.x + offsetX);
      const tileY = override?.y ?? (obj.y + offsetY);
      const tileSprite = this.createTileSprite(obj, textureKey, tileX, tileY, 'both', override);
      tileSprite.setData('uid', UID.next());
      return tileSprite;
    }

    if (tileHorizontal) {
      // Repetir solo horizontalmente (usar width del JSON o override, height de textura)
      const tileX = override?.x ?? (obj.x + offsetX);
      const tileY = override?.y ?? (obj.y + offsetY);
      const tileSprite = this.createTileSprite(obj, textureKey, tileX, tileY, 'horizontal', override);
      tileSprite.setData('uid', UID.next());
      return tileSprite;
    }

    // Posición para sprites normales
    // En C3, (x, y) ya es la posición del origin del sprite, no top-left
    // Si hay x/y directo en override, usarlo; sino usar offset
    const x = override?.x ?? (obj.x + offsetX);
    const y = override?.y ?? (obj.y + offsetY);

    // Crear sprite o imagen normal
    let gameObj: Phaser.GameObjects.Image | Phaser.GameObjects.Sprite;

    // Si tiene animación, crear sprite
    if (obj.animation) {
      gameObj = this.scene.add.sprite(x, y, textureKey);
      if ((gameObj as Phaser.GameObjects.Sprite).anims.exists(obj.animation)) {
        (gameObj as Phaser.GameObjects.Sprite).play(obj.animation);
      }
    } else {
      gameObj = this.scene.add.image(x, y, textureKey);
    }

    // Aplicar propiedades con width/height/scale override
    const baseWidth = override?.width ?? Math.abs(obj.width);
    const baseHeight = override?.height ?? Math.abs(obj.height);
    const scaledWidth = baseWidth * scaleOverride;
    const scaledHeight = baseHeight * scaleOverride;
    gameObj.setDisplaySize(scaledWidth, scaledHeight);

    // Usar override de origin si existe, sino usar valor del objeto o default 0.5
    const finalOriginX = override?.originX ?? obj.originX ?? 0.5;
    const finalOriginY = override?.originY ?? obj.originY ?? 0.5;
    gameObj.setOrigin(finalOriginX, finalOriginY);

    // Manejar flip si width o height son negativos
    if (obj.width < 0) gameObj.setFlipX(true);
    if (obj.height < 0) gameObj.setFlipY(true);

    if (obj.angle) {
      gameObj.setRotation(obj.angle);
    }

    // Asignar UID dinámico
    gameObj.setData('uid', UID.next());

    return gameObj;
  }

  /**
   * Verifica si un tipo debe repetirse solo horizontalmente
   */
  private isTileHorizontal(textureKey: string): boolean {
    // Verificar por nombre exacto
    if (LevelLoader.TILE_HORIZONTAL_NAMES.has(textureKey)) {
      return true;
    }
    // Verificar por prefijo (ej: "tm" para tmtreegreen, tmvalla, etc.)
    for (const prefix of LevelLoader.TILE_HORIZONTAL_PREFIXES) {
      if (textureKey.startsWith(prefix)) {
        return true;
      }
    }
    return false;
  }

  /**
   * Crea un TileSprite que repite la textura
   * @param mode 'both' = repite en X e Y, 'horizontal' = repite solo en X
   */
  private createTileSprite(
    obj: ObjectInstance,
    textureKey: string,
    x: number,
    y: number,
    mode: 'both' | 'horizontal',
    override?: LayoutOverride
  ): Phaser.GameObjects.TileSprite {
    // Obtener dimensiones originales de la textura
    const texture = this.scene.textures.get(textureKey);
    const frame = texture.get();
    const texWidth = frame.width;
    const texHeight = frame.height;

    // Calcular escala desde el JSON (scaleX/scaleY)
    const scaleX = obj.scaleX ?? 1;
    const scaleY = obj.scaleY ?? 1;

    // Determinar dimensiones del TileSprite (con override si existe)
    let tileWidth: number;
    let tileHeight: number;

    if (mode === 'both') {
      // Repetir en ambas direcciones: usar dimensiones del JSON o override
      tileWidth = override?.width ?? Math.abs(obj.width);
      tileHeight = override?.height ?? Math.abs(obj.height);
    } else {
      // Repetir solo horizontal: usar width del JSON o override, height de la textura escalada
      tileWidth = override?.width ?? Math.abs(obj.width);
      tileHeight = override?.height ?? texHeight * scaleY;
    }

    // Crear TileSprite
    const tileSprite = this.scene.add.tileSprite(
      x,
      y,
      tileWidth,
      tileHeight,
      textureKey
    );

    // Escalar los tiles internamente
    tileSprite.setTileScale(scaleX, scaleY);
    tileSprite.setOrigin(obj.originX ?? 0, obj.originY ?? 0);

    if (obj.angle) {
      tileSprite.setRotation(obj.angle);
    }

    // Apply physics if has solid behavior
    this.applySolidPhysics(tileSprite, textureKey);

    return tileSprite;
  }

  /**
   * Apply solid physics to a TileSprite if it has solid behavior
   * This makes TileSprites like tiledfloor act as physical ground
   * SSOT: reads behaviors.solid from layouts-c3data.json
   */
  private applySolidPhysics(
    tileSprite: Phaser.GameObjects.TileSprite,
    textureKey: string
  ): void {
    // SSOT: use layouts-c3data.json for solid behavior
    if (!this.hasSolidBehavior(textureKey)) {
      return;
    }

    // Add static physics body
    this.scene.physics.add.existing(tileSprite, true); // true = static body

    // Register in CollisionSystem
    const collisionSystem = this.scene.registry.get('collisionSystem') as CollisionSystem | undefined;
    if (!collisionSystem) {
      console.error('[LevelLoader] CollisionSystem not found in registry');
      return;
    }
    collisionSystem.registerSolid(tileSprite);

    // Notify TractorBeam that a blocker was added
    this.scene.events.emit('tractorBlockerAdded', tileSprite);
  }

  /**
   * Crea sprites de tiles para un tilemap de C3
   * Los tiles se posicionan según la grilla y solo se crean para tiles con valor > 0
   * Soporta scale override desde entities-overrides.json
   */
  private createTilemap(tilemap: TilemapInstance): Phaser.GameObjects.Image[] {
    const textureKey = this.getTextureKey(tilemap.type);
    const images: Phaser.GameObjects.Image[] = [];

    // Verificar si la textura existe
    if (!this.scene.textures.exists(textureKey)) {
      console.warn(`[LevelLoader] Tilemap texture not found: ${textureKey}`);
      return images;
    }

    // Obtener override con prioridad: instance > level > global
    const override = this.getTilemapOverride(textureKey, tilemap.id);
    const scale = override?.scale ?? 1;
    const offsetX = override?.offsetX ?? 0;
    const offsetY = override?.offsetY ?? 0;

    const { x: baseX, y: baseY, tileWidth, tileHeight, gridWidth, gridHeight: originalGridHeight, tiles: baseTiles } = tilemap;

    // Usar overrides de Y y tiles si existen
    const effectiveY = override?.y ?? baseY;
    const effectiveTiles = override?.tiles ?? baseTiles;

    // Si hay override de gridHeight, usar ese valor y filtrar filas desde abajo
    const effectiveGridHeight = override?.gridHeight ?? originalGridHeight;
    const startRow = Math.max(0, originalGridHeight - effectiveGridHeight);

    // Aplicar scale a dimensiones del tile
    const scaledTileWidth = tileWidth * scale;
    const scaledTileHeight = tileHeight * scale;

    // Iterar sobre cada tile en la grilla
    for (let i = 0; i < effectiveTiles.length; i++) {
      const tileValue = effectiveTiles[i];

      // Calcular posición en la grilla
      const col = i % gridWidth;
      const row = Math.floor(i / gridWidth);

      // Solo crear sprite si el tile no está vacío y está en las filas a renderizar
      if (tileValue > 0 && row >= startRow) {

        // Calcular posición en pixeles
        const x = baseX + (col * scaledTileWidth) + offsetX;
        const y = effectiveY + (row * scaledTileHeight) + offsetY;

        // Crear imagen del tile
        const image = this.scene.add.image(x, y, textureKey);
        image.setOrigin(0, 0);
        image.setScale(scale);

        // Asignar UID dinámico a cada tile
        image.setData('uid', UID.next());

        images.push(image);
      }
    }

    return images;
  }

  /**
   * Crea un placeholder para assets no cargados
   */
  private createPlaceholder(obj: ObjectInstance): Phaser.GameObjects.Rectangle {
    // En C3, (x, y) ya es la posición del origin del sprite
    const x = obj.x;
    const y = obj.y;

    // Color según tipo de objeto
    let color = 0x888888;
    if (obj.type.includes('Background') || obj.type.includes('bg')) {
      color = 0x336699; // Azul para backgrounds
    } else if (obj.type.includes('human')) {
      color = 0x66aa66; // Verde para NPCs
    } else if (obj.type.includes('tree') || obj.type.includes('house')) {
      color = 0x996633; // Marrón para decoraciones
    }

    const rect = this.scene.add.rectangle(x, y, obj.width, obj.height, color, 0.3);
    rect.setStrokeStyle(1, color, 0.8);
    rect.setOrigin(obj.originX ?? 0.5, obj.originY ?? 0.5);

    // Etiqueta con el tipo
    const label = this.scene.add.text(x, y, obj.type, {
      fontSize: '10px',
      color: '#ffffff',
      backgroundColor: '#000000aa',
      padding: { x: 2, y: 1 },
    });
    label.setOrigin(0.5);
    label.setScale(Math.min(1, obj.width / label.width));

    // El label ya está posicionado sobre el rect
    return rect;
  }

  /**
   * Obtiene la key de textura para un tipo de C3
   */
  private getTextureKey(c3Type: string): string {
    // Mapeo de tipos C3 a keys de Phaser
    // Por ahora usamos el mismo nombre
    return c3Type.toLowerCase();
  }

  /**
   * Renderiza los coleccionables
   */
  private renderCollectibles(
    collectibles: CollectibleData[],
    container: Phaser.GameObjects.Container
  ): Phaser.GameObjects.Sprite[] {
    const sprites: Phaser.GameObjects.Sprite[] = [];

    for (const item of collectibles) {
      // Verificar si el objeto está excluido
      if (this.isExcluded(item.id)) {
        continue;
      }

      const textureKey = this.getTextureKey(item.type);
      const x = item.x;
      const y = item.y;

      // Verificar si el asset existe
      if (this.scene.textures.exists(textureKey)) {
        const sprite = this.scene.add.sprite(x, y, textureKey);
        sprite.setDisplaySize(item.width, item.height);
        sprite.setData('collectibleData', item);
        sprite.setData('uid', UID.next());
        container.add(sprite);
        sprites.push(sprite);
      } else {
        // Placeholder
        const rect = this.scene.add.rectangle(x, y, item.width, item.height, 0xffcc00, 0.5);
        rect.setStrokeStyle(2, 0xffcc00);
        container.add(rect);
      }
    }

    return sprites;
  }

  /**
   * Extrae NPCs de las capas y los renderiza
   * NOTA: Ahora devuelve vacío porque RuntimeManagerSpawn se encarga de las entidades
   */
  private extractAndRenderNPCs(_container: Phaser.GameObjects.Container): Phaser.GameObjects.Sprite[] {
    // RuntimeManagerSpawn se encarga de crear las entidades dinámicamente
    return [];
  }

  /**
   * Configura spawns de enemigos (para ser procesados por el sistema de juego)
   */
  getSpawns(): SpawnData[] {
    return this.levelData?.spawns ?? [];
  }

  /**
   * Obtiene los datos del nivel cargado
   */
  getData(): LevelData | null {
    return this.levelData;
  }
}

/**
 * Función helper para cargar un nivel de forma simple
 */
export async function loadLevel(
  scene: Phaser.Scene,
  levelId: string
): Promise<LoadedLevel> {
  const loader = new LevelLoader(scene);
  return loader.loadLevel(levelId);
}
